import React from "react";
import { FW, StepProgress } from "./ui/primitives";
import { StepScope } from "./ui/steps/step0-scope";
import { StepRol } from "./ui/steps/step1-rol";
import { StepNiveau } from "./ui/steps/step2-niveau";
import { StepTimesheet } from "./ui/steps/step3-timesheet";
import { StepFestivalWerkdagen } from "./ui/steps/step3-festival-werkdagen";
import { StepFestivalDienstverband } from "./ui/steps/step3-festival-dienstverband";
import { StepResult } from "./ui/steps/step4-result";
import { StepFestivalZzpResult } from "./ui/steps/step4-festival-zzp";
import { StepFestivalLoondienstResult } from "./ui/steps/step4-festival-loondienst";

const stepsVoor = (s) => {
  if (s?.type === "festival") {
    if (s?.vorm === "loondienst") {
      return ["Productie of Festival", "Functieomschrijving", "Schaal", "Dienstverband", "Salaris"];
    }
    return ["Productie of Festival", "Functieomschrijving", "Tarief", "Werkdagen", "Openingsbod"];
  }
  if (s?.type === "film" && s?.perspectief === "opdrachtgever") {
    return ["Productie of Festival", "Opdrachtomschrijving", "Fair Pay tarief"];
  }
  return ["Productie of Festival", "Opdrachtomschrijving", "Timesheet", "Zwaarteniveau", "Openingsbod"];
};

// Deep-link via query params: ?type=film-av&origin=zzp → sla step0 over.
//   type:   film-av | film → "film"; festival → "festival"
//   origin: film     → zzp | opdrachtgever
//           festival → zzp | loondienst
// Beide geldig → step1; alleen geldige type → step0 op de vorm/perspectief-keuze.
const TYPE_MAP = { "film-av": "film", film: "film", festival: "festival" };
function parseUrlPrefill() {
  const p = new URLSearchParams(location.search);
  const type = TYPE_MAP[(p.get("type") || "").toLowerCase()];
  if (!type) return null;
  const origin = (p.get("origin") || "").toLowerCase();
  if (type === "film") {
    if (origin === "zzp")           return { state: { type: "film", perspectief: "uitvoerder" }, step: 1 };
    if (origin === "opdrachtgever") return { state: { type: "film", perspectief: "opdrachtgever" }, step: 1 };
    return { state: { type: "film" }, step: 0 };
  }
  if (origin === "zzp")        return { state: { type: "festival", vorm: "zzp", perspectief: "uitvoerder" }, step: 1 };
  if (origin === "loondienst") return { state: { type: "festival", vorm: "loondienst", perspectief: "uitvoerder" }, step: 1 };
  return { state: { type: "festival" }, step: 0 };
}

export function App() {
  const urlState = React.useMemo(() => {
    const m = location.hash.match(/#berekening=([^&]+)/);
    if (!m) return null;
    try { return JSON.parse(decodeURIComponent(escape(atob(m[1])))); }
    catch { return null; }
  }, []);

  // #berekening (volledige state) heeft voorrang op de query-param-deeplink.
  const urlPrefill = React.useMemo(() => (urlState ? null : parseUrlPrefill()), [urlState]);

  const [state, setState] = React.useState(() => {
    if (urlState) return urlState;
    if (urlPrefill) return urlPrefill.state;
    try {
      const raw = JSON.parse(localStorage.getItem("fw-calc") || "null");
      if (!raw) return {};
      // v1 -> v2: oude state had platte rol/niveau zonder rollen-array, verse start
      if (!Array.isArray(raw.rollen) && (raw.rol || raw.niveau || raw.draaidagen != null)) return {};
      return raw;
    } catch { return {}; }
  });
  const [step, setStep] = React.useState(() => {
    if (urlState) return 4;
    if (urlPrefill) return urlPrefill.step;
    try {
      const raw = JSON.parse(localStorage.getItem("fw-step") || "0");
      const calcRaw = JSON.parse(localStorage.getItem("fw-calc") || "null") || {};
      const maxStep = stepsVoor(calcRaw).length - 1;
      return Number.isInteger(raw) && raw >= 0 && raw <= maxStep ? raw : 0;
    } catch { return 0; }
  });

  // Deep-link-params na toepassen uit de URL strippen, zodat een refresh de
  // voortgang volgt (localStorage) i.p.v. terug te springen naar de prefill-stap.
  React.useEffect(() => {
    if (urlPrefill && location.search) {
      history.replaceState(null, "", location.pathname + location.hash);
    }
  }, []);

  React.useEffect(() => { localStorage.setItem("fw-calc", JSON.stringify(state)); }, [state]);
  React.useEffect(() => { localStorage.setItem("fw-step", JSON.stringify(step)); }, [step]);
  React.useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const STEPS = stepsVoor(state);
  const next    = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back    = () => setStep((s) => Math.max(0, s - 1));
  const restart = () => { setState({}); setStep(0); location.hash = ""; };

  const isFestival = state.type === "festival";
  const isLoondienst = isFestival && state.vorm === "loondienst";
  const isFilmClient = state.type === "film" && state.perspectief === "opdrachtgever";

  return (
    <div>
      <TopBar onReset={restart} />
      {step > 0 && (
        <div className="progress-bar">
          <div className="progress-bar-inner" style={{ display: "flex", justifyContent: "center" }}>
            {/* De eerste stap ("Productie of Festival") wordt bewust niet in de
                stepper getoond: die keuze is al gemaakt op de startpagina en
                verwarde testers (klik leidde naar zzp/opdrachtgever i.p.v. type). */}
            <StepProgress steps={STEPS.slice(1)} current={step - 1} onJumpTo={(i) => setStep(i + 1)} />
          </div>
        </div>
      )}

      <div className="app-shell">
        {step === 0 && <StepScope  state={state} setState={setState} next={next} />}
        {step === 1 && <StepRol    state={state} setState={setState} next={next} back={back} />}
        {step === 2 && (
          isFestival
            ? <StepNiveau    state={state} setState={setState} next={next} back={back} />
            : isFilmClient
              ? <StepNiveau    state={state} setState={setState} next={next} back={back} />
              : <StepTimesheet state={state} setState={setState} next={next} back={back} />
        )}
        {step === 3 && !isFilmClient && (
          isFestival
            ? (isLoondienst
                ? <StepFestivalDienstverband state={state} setState={setState} next={next} back={back} />
                : <StepFestivalWerkdagen     state={state} setState={setState} next={next} back={back} />)
            : <StepNiveau state={state} setState={setState} next={next} back={back} />
        )}
        {step === 4 && !isFilmClient && (
          isFestival
            ? (isLoondienst
                ? <StepFestivalLoondienstResult state={state} setState={setState} back={back} restart={restart} />
                : <StepFestivalZzpResult        state={state} setState={setState} back={back} restart={restart} />)
            : <StepResult state={state} setState={setState} back={back} goToStep={setStep} restart={restart} />
        )}
      </div>
    </div>
  );
}

export const TopBar = ({ showToolsLink = false, onReset }) => (
  <div className="top-bar" style={{
    padding: "20px 40px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    gap: 12, maxWidth: 1240, margin: "0 auto",
  }}>
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <a href="https://www.filmwerkwijs.nl" aria-label="Naar filmwerkwijs.nl" style={{ display: "inline-flex" }}>
        <img src="/assets/logo-filmwerkwijs.svg" alt="Filmwerkwijs" className="top-bar-logo" style={{ height: "120px", width: "auto" }} />
      </a>
    </div>
    <div style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
      {showToolsLink && (
        <a
          href="/"
          className="top-bar-tools-link"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px 6px 10px", borderRadius: 6,
            background: FW.white, color: FW.navy, border: `1px solid ${FW.taupe}`,
            fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
            fontFamily: "var(--font-sans)", textDecoration: "none",
            transition: "background .15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = FW.beige2; }}
          onMouseLeave={e => { e.currentTarget.style.background = FW.white; }}
        >
          <i className="ri-arrow-left-line" style={{ fontSize: 14 }} /> Alle tools
        </a>
      )}
      {/* Tagline + permanente "Nieuwe berekening"-knop eronder. De knop reset de
          hele flow (zelfde interactie als op de laatste stap) en staat overal in beeld. */}
      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
        <div className="top-bar-tagline" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: FW.navy2, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Privacyverklaring op filmwerkwijs.nl, vanaf elke pagina van de tool bereikbaar. */}
          <a
            href="https://www.filmwerkwijs.nl/privacy/"
            target="_blank"
            rel="noreferrer"
            style={{ color: FW.navy2, textDecoration: "underline", textUnderlineOffset: 3 }}
          >
            Privacyverklaring
          </a>
          <span className="top-bar-fairpay" aria-hidden="true" style={{ color: FW.gray500 }}>·</span>
          <i className="top-bar-fairpay ri-shield-check-line" />
          <span className="top-bar-fairpay">Fair Pay richtlijnen 2026</span>
        </div>
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            className="top-bar-reset"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px 6px 10px", borderRadius: 6,
              background: FW.yellow, color: FW.navy, border: 0,
              fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
              fontFamily: "var(--font-sans)", cursor: "pointer",
              transition: "background .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#E6D92C"; }}
            onMouseLeave={e => { e.currentTarget.style.background = FW.yellow; }}
          >
            <i className="ri-refresh-line" style={{ fontSize: 14 }} /> Nieuwe berekening
          </button>
        )}
      </div>
    </div>
  </div>
);
